import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, HeartFill } from 'react-bootstrap-icons';
import { mediaUrl } from '../../config/api';
import './ProductCard.scss';

const STAR_COUNT = 5;

/**
 * Formats a price defensively. The API occasionally returns a product
 * without a resolved variant, which used to render a literal "$NaN".
 */
const formatPrice = (value) => {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount.toFixed(2) : null;
};

const StockNote = ({ quantity, isSingleProduct }) => {
  if (!isSingleProduct) return null;
  if (quantity === 0 || quantity == null) {
    return <p className="product-card__stock product-card__stock--out">Out of stock</p>;
  }
  if (quantity < 15) {
    return <p className="product-card__stock">Only {quantity} left</p>;
  }
  return null;
};

/**
 * The single product tile used by the catalogue, landing rails,
 * search results and "you may also like".
 *
 * Before this existed the same ~80 lines of markup were pasted into
 * five components, each with slightly different bugs.
 */
const ProductCard = ({
  product,
  isWishlisted = false,
  onToggleWishlist,
  onAddToCart,
  isAddingToCart = false,
  showWishlist = true,
  showActions = true,
  imageLoading = 'lazy',
}) => {
  if (!product) return null;

  const {
    product_code: productCode,
    product_name: productName,
    product_image_1: image,
    product_discount: discountRaw,
    variant,
    top_review: topReview,
    single_product: singleProduct,
  } = product;

  // `0` is a valid discount value and must not render as a badge —
  // the previous `{discount && ...}` form leaked a literal "0" into
  // the markup for those products.
  const discount = Number(discountRaw) > 0 ? Number(discountRaw) : 0;
  const basePrice = Number(variant?.price);
  const hasPrice = Number.isFinite(basePrice);
  const finalPrice = formatPrice(hasPrice ? basePrice * (1 - discount / 100) : NaN);
  const strikePrice = discount > 0 ? formatPrice(basePrice) : null;

  const isSingleProduct = Number(singleProduct) > 0;
  const canAddDirectly = Number(singleProduct) === 1;
  const rating = Number(topReview?.rating) || 0;
  const imageSrc = mediaUrl(image);
  const detailsUrl = `/productshow/${productCode}`;

  return (
    <article className="product-card">
      {discount > 0 && <span className="product-card__badge badge badge--sale">{discount}% off</span>}

      {showWishlist && (
        <button
          type="button"
          className={`product-card__wishlist${isWishlisted ? ' is-active' : ''}`}
          onClick={() => onToggleWishlist?.(productCode)}
          aria-pressed={isWishlisted}
          aria-label={
            isWishlisted
              ? `Remove ${productName} from wishlist`
              : `Add ${productName} to wishlist`
          }
        >
          {isWishlisted ? <HeartFill size={18} aria-hidden="true" /> : <Heart size={18} aria-hidden="true" />}
        </button>
      )}

      <Link className="product-card__link" to={detailsUrl}>
        <div className="product-card__media">
          {imageSrc ? (
            <img src={imageSrc} alt={productName || 'Product'} loading={imageLoading} decoding="async" />
          ) : (
            <div className="product-card__media-fallback" aria-hidden="true" />
          )}
        </div>

        <div className="product-card__body">
          <h3 className="product-card__title">{productName}</h3>

          <p className="product-card__price">
            {finalPrice ? (
              <>
                <span className="product-card__price-now">${finalPrice}</span>
                {strikePrice && <span className="product-card__price-was">${strikePrice}</span>}
              </>
            ) : (
              <span className="product-card__price-now">Price on request</span>
            )}
          </p>

          <div
            className="product-card__rating"
            role="img"
            aria-label={rating ? `Rated ${rating} out of 5` : 'No reviews yet'}
          >
            {Array.from({ length: STAR_COUNT }).map((_, index) => (
              <span key={index} aria-hidden="true" className={index < rating ? 'is-filled' : ''}>
                {index < rating ? '★' : '☆'}
              </span>
            ))}
          </div>
        </div>
      </Link>

      {showActions && (
        <div className="product-card__actions">
          {canAddDirectly ? (
            <button
              type="button"
              className="btn btn--primary btn--sm btn--block"
              onClick={() => onAddToCart?.(variant?.id, productCode)}
              disabled={isAddingToCart}
              aria-busy={isAddingToCart}
            >
              {isAddingToCart ? (
                <>
                  <span className="btn__spinner" aria-hidden="true" />
                  Adding…
                </>
              ) : (
                'Add to Cart'
              )}
            </button>
          ) : (
            <Link className="btn btn--outline btn--sm btn--block" to={detailsUrl}>
              Choose Options
            </Link>
          )}
        </div>
      )}

      <StockNote quantity={variant?.quantity} isSingleProduct={isSingleProduct} />
    </article>
  );
};

export default React.memo(ProductCard);
